import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Users, CheckCircle, Printer, Shield, UserCheck, Building2 } from 'lucide-react';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Loading from '../components/common/Loading';
import DataViewModal from '../components/dashboard/DataViewModal';
import ViewAsIndicator from '../components/viewas/ViewAsIndicator';
import RoleSelector from '../components/viewas/RoleSelector';
import { eventAPI, analyticsAPI } from '../api/endpoints';
import { useAuth } from '../context/AuthContext';
import { useEffectiveAuth } from '../hooks/useEffectiveAuth';
import { useEvent } from '../context/EventContext';
import { USER_ROLE_LABELS } from '../utils/constants';

const Dashboard = () => {
  const { user } = useAuth();
  const { effectiveUser, effectivePermissions, isViewingAs } = useEffectiveAuth();
  const { selectedEvent, setSelectedEvent } = useEvent();
  const [modalData, setModalData] = useState({
    isOpen: false,
    title: '',
    data: null,
    type: '',
    isLoading: false,
    error: null
  });

  // Fetch user's events based on role and permissions
  const { data: eventsData, isLoading: eventsLoading, error: eventsError } = useQuery({
    queryKey: ['user-events', user?.id],
    queryFn: async () => {
      if (effectivePermissions.isSuperAdmin()) {
        // Super admins see all events
        const response = await eventAPI.getAll({ is_active: true });
        return response.data;
      } else {
        // Other users see only their assigned events (backend handles filtering)
        const response = await eventAPI.getAll({ is_active: true });
        return response.data;
      }
    },
    enabled: !!user,
    retry: 1,
  });

  // Fetch analytics data for selected event or user's events
  const { data: analyticsData, isLoading: analyticsLoading } = useQuery({
    queryKey: ['user-analytics', selectedEvent, user?.id, user?.role],
    queryFn: async () => {
      if (selectedEvent) {
        // Get analytics for specific event
        const response = await analyticsAPI.getEventAnalytics(selectedEvent);
        return response.data;
      } else if (effectivePermissions.isSuperAdmin()) {
        // Super admin gets system-wide analytics
        const response = await analyticsAPI.getDashboard();
        return response.data;
      } else {
        // Other users get their own user-specific analytics
        // This will return stats only for events they have access to
        const response = await analyticsAPI.getDashboard();
        return response.data;
      }
    },
    enabled: !!user,
    retry: 1,
  });

  // Auto-select first event when events load (but not for super admins - they see system-wide stats by default)
  useEffect(() => {
    if (eventsData?.events?.length > 0 && !selectedEvent && !effectivePermissions.isSuperAdmin()) {
      setSelectedEvent(eventsData.events[0].id);
    }
  }, [eventsData, selectedEvent, effectivePermissions, setSelectedEvent]);

  // Reset selected event if it's no longer in user's accessible events
  useEffect(() => {
    if (selectedEvent && eventsData?.events?.length > 0) {
      const eventExists = eventsData.events.some(event => event.id === selectedEvent);
      if (!eventExists) {
        setSelectedEvent(eventsData.events[0].id);
      }
    }
  }, [selectedEvent, eventsData, setSelectedEvent]);

  // Handle card clicks - now event-specific for multi-tenancy
  const handleCardClick = async (type) => {
    setModalData({
      isOpen: true,
      title: getModalTitle(type),
      data: null,
      type: type,
      isLoading: true,
      error: null
    });

    try {
      let response;
      switch (type) {
        case 'events':
          if (effectivePermissions.isSuperAdmin()) {
            response = await analyticsAPI.getAllEvents();
            setModalData(prev => ({ ...prev, data: response.data.events, isLoading: false }));
          } else {
            // Show only user's assigned events with enhanced data
            const userEvents = eventsData?.events?.map(event => ({
              ...event,
              attendee_count: event.attendees_count ?? event.attendee_count ?? event.attendeeCount ?? 0,
              creator: user?.full_name,
              created_at: event.created_at
            })) || [];
            setModalData(prev => ({ ...prev, data: userEvents, isLoading: false }));
          }
          break;
        case 'event_attendees':
          if (selectedEvent) {
            // Check if user can access this event
            const canAccess = effectivePermissions.isSuperAdmin() ||
              eventsData?.events?.some(event => event.id === selectedEvent);
            if (canAccess) {
              response = await analyticsAPI.getDemographics(selectedEvent, {});
              // The attendeeAnalytics endpoint returns attendees array directly
              const attendeesList = response.data.attendees || [];
              setModalData(prev => ({ ...prev, data: attendeesList, isLoading: false }));
            } else {
              setModalData(prev => ({ ...prev, isLoading: false, error: { message: 'Access denied to this event' } }));
            }
          } else if (effectivePermissions.isSuperAdmin()) {
            // Super admin without event selected - show all attendees
            response = await analyticsAPI.getAllAttendees();
            setModalData(prev => ({ ...prev, data: response.data.attendees || [], isLoading: false }));
          } else {
            setModalData(prev => ({ ...prev, isLoading: false, error: { message: 'No event selected' } }));
          }
          break;
        case 'event_admins':
          if (effectivePermissions.isSuperAdmin()) {
            response = await analyticsAPI.getEventAdmins();
            setModalData(prev => ({ ...prev, data: response.data.users, isLoading: false }));
          } else {
            setModalData(prev => ({ ...prev, isLoading: false, error: { message: 'Access denied' } }));
          }
          break;
        case 'users':
          if (effectivePermissions.isSuperAdmin()) {
            response = await analyticsAPI.getAllUsers();
            setModalData(prev => ({ ...prev, data: response.data.users, isLoading: false }));
          } else {
            setModalData(prev => ({ ...prev, isLoading: false, error: { message: 'Access denied' } }));
          }
          break;
        default:
          setModalData(prev => ({ ...prev, isLoading: false, error: { message: 'Unknown data type' } }));
      }
    } catch (error) {
      setModalData(prev => ({ ...prev, isLoading: false, error }));
    }
  };

  const getModalTitle = (type) => {
    switch (type) {
      case 'events': return effectivePermissions.isSuperAdmin() ? 'All Events' : 'My Events';
      case 'event_admins': return 'Event Administrators';
      case 'event_attendees':
        if (selectedEvent) {
          return `Attendees - ${eventsData?.events?.find(e => e.id === selectedEvent)?.name || 'Event'}`;
        }
        return effectivePermissions.isSuperAdmin() ? 'All Attendees' : 'Event Attendees';
      case 'users': return 'System Users';
      default: return 'Data View';
    }
  };

  const closeModal = () => {
    setModalData({
      isOpen: false,
      title: '',
      data: null,
      type: '',
      isLoading: false,
      error: null
    });
  };

  if (eventsLoading) {
    return (
      <Layout>
        <Loading fullScreen text="Loading dashboard..." />
      </Layout>
    );
  }

  if (eventsError) {
    return (
      <Layout>
        <Card>
          <Card.Content className="text-center py-12">
            <div className="text-red-600 mb-4">
              <svg className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Unable to Load Data
            </h3>
            <p className="text-gray-600 mb-4">
              {(eventsError as any).response?.status === 401
                ? 'Session expired. Please login again.'
                : 'Failed to connect to the server. Make sure the backend is running.'}
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Error: {eventsError.message}
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left max-w-md mx-auto">
              <p className="text-sm font-semibold text-yellow-800 mb-2">Quick Fix:</p>
              <ol className="text-sm text-yellow-700 space-y-1 list-decimal list-inside">
                <li>Make sure backend is running: <code className="bg-yellow-100 px-1 rounded">npm run dev</code></li>
                <li>Check if database is seeded: <code className="bg-yellow-100 px-1 rounded">npm run seed</code></li>
                <li>Verify MySQL is running in XAMPP</li>
              </ol>
            </div>
          </Card.Content>
        </Card>
      </Layout>
    );
  }

  // Role-specific stats - now properly multi-tenant
  const getRoleSpecificStats = () => {
    const events = eventsData?.events || [];
    const eventData = analyticsData || {};

    if (effectivePermissions.isSuperAdmin()) {
      // When no event is selected, show system-wide stats
      // When an event is selected, show event-specific stats
      if (!selectedEvent) {
        return [
          {
            label: 'Total Events',
            value: events.length,
            icon: Calendar,
            color: 'text-blue-600',
            bgColor: 'bg-blue-100',
            clickable: true,
            onClick: () => handleCardClick('events'),
          },
          {
            label: 'Organizations',
            value: eventData?.summary?.totalOrganizations || 0,
            icon: Building2,
            color: 'text-indigo-600',
            bgColor: 'bg-indigo-100',
            clickable: true,
            onClick: () => handleCardClick('event_admins'),
          },
          {
            label: 'Event Admins',
            value: eventData?.summary?.eventAdmins || 0,
            icon: Shield,
            color: 'text-purple-600',
            bgColor: 'bg-purple-100',
            clickable: true,
            onClick: () => handleCardClick('event_admins'),
          },
          {
            label: 'Total Attendees',
            value: eventData?.stats?.totalAttendees || 0,
            icon: Users,
            color: 'text-green-600',
            bgColor: 'bg-green-100',
            clickable: true,
            onClick: () => handleCardClick('event_attendees'),
          },
          {
            label: 'System Users',
            value: eventData?.summary?.totalUsers || 0,
            icon: UserCheck,
            color: 'text-orange-600',
            bgColor: 'bg-orange-100',
            clickable: true,
            onClick: () => handleCardClick('users'),
          },
          {
            label: 'Total Checked In',
            value: eventData?.summary?.checkedIn || 0,
            icon: CheckCircle,
            color: 'text-teal-600',
            bgColor: 'bg-teal-100',
          },
          {
            label: 'Badges Printed',
            value: eventData?.summary?.badgesPrinted || 0,
            icon: Printer,
            color: 'text-pink-600',
            bgColor: 'bg-pink-100',
          },
        ];
      } else {
        // Event-specific stats for super admin
        const selectedEventData = events.find(e => e.id === selectedEvent);
        return [
          {
            label: 'Event',
            value: selectedEventData?.name || 'Selected Event',
            icon: Calendar,
            color: 'text-blue-600',
            bgColor: 'bg-blue-100',
            isText: true,
          },
          {
            label: 'Event Attendees',
            value: eventData?.stats?.totalAttendees || selectedEventData?.attendees_count || 0,
            icon: Users,
            color: 'text-green-600',
            bgColor: 'bg-green-100',
            clickable: true,
            onClick: () => handleCardClick('event_attendees'),
          },
          {
            label: 'Checked In',
            value: eventData?.stats?.checkedIn || 0,
            icon: CheckCircle,
            color: 'text-green-600',
            bgColor: 'bg-green-100',
            subtitle: eventData?.stats?.checkInRate ? `${eventData.stats.checkInRate}% rate` : undefined,
          },
          {
            label: 'Badges Printed',
            value: eventData?.summary?.badgesPrinted || 0,
            icon: Printer,
            color: 'text-purple-600',
            bgColor: 'bg-purple-100',
          },
        ];
      }
    } else if (effectivePermissions.isEventAdmin()) {
      // Event-specific data for selected event
      const selectedEventData = selectedEvent ? events.find(e => e.id === selectedEvent) : null;

      return [
        {
          label: 'My Events',
          value: events.length,
          icon: Building2,
          color: 'text-blue-600',
          bgColor: 'bg-blue-100',
          clickable: true,
          onClick: () => handleCardClick('events'),
        },
        {
          label: selectedEvent ? 'Event Attendees' : 'Total Attendees',
          value: selectedEvent ? (eventData?.stats?.totalAttendees || selectedEventData?.attendees_count || 0) :
            events.reduce((sum, event) => sum + (event.attendees_count || 0), 0),
          icon: Users,
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          clickable: !!selectedEvent,
          onClick: selectedEvent ? () => handleCardClick('event_attendees') : undefined,
        },
        {
          label: 'Checked In',
          value: eventData?.checkedInCount || eventData?.summary?.checkedIn || 0,
          icon: CheckCircle,
          color: 'text-teal-600',
          bgColor: 'bg-teal-100',
          subtitle: (eventData?.checkInRate || eventData?.summary?.checkInRate) ? `${eventData?.checkInRate || eventData?.summary?.checkInRate}% rate` : undefined,
        },
        {
          label: 'Event Staff',
          value: eventData?.staffCount || eventData?.summary?.staffCount || 0,
          icon: UserCheck,
          color: 'text-purple-600',
          bgColor: 'bg-purple-100',
        },
        {
          label: 'Badges Printed',
          value: eventData?.badgesPrinted || eventData?.summary?.badgesPrinted || 0,
          icon: Printer,
          color: 'text-pink-600',
          bgColor: 'bg-pink-100',
        },
      ];
    } else {
      // For ushers, front desk, etc. - show only their assigned event data
      const selectedEventData = selectedEvent ? events.find(e => e.id === selectedEvent) : null;

      return [
        {
          label: 'Assigned Events',
          value: events.length,
          icon: Calendar,
          color: 'text-blue-600',
          bgColor: 'bg-blue-100',
        },
        {
          label: selectedEvent ? 'Event Attendees' : 'Total Attendees',
          value: selectedEvent ? (eventData?.stats?.totalAttendees || selectedEventData?.attendees_count || 0) :
            events.reduce((sum, event) => sum + (event.attendees_count || 0), 0),
          icon: Users,
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          clickable: !!selectedEvent,
          onClick: selectedEvent ? () => handleCardClick('event_attendees') : undefined,
        },
        {
          label: 'Checked In Today',
          value: eventData?.checkedInCount || 0,
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-100',
        },
        {
          label: 'Badges Printed',
          value: eventData?.badgesPrinted || 0,
          icon: Printer,
          color: 'text-purple-600',
          bgColor: 'bg-purple-100',
        },
      ];
    }
  };

  const stats = getRoleSpecificStats();

  return (
    <Layout>
      <div className="space-y-6">
        {/* ViewAs Indicator - shows when user is viewing as another role */}
        <ViewAsIndicator />

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {effectivePermissions.isAdmin() && 'Dashboard'}
              {!effectivePermissions.isAdmin() && 'Dashboard'}
            </h1>
            {/* <p className="text-gray-600 mt-1 text-sm sm:text-base">
              Welcome back, {user?.full_name}
              <span className="hidden sm:inline"> ({USER_ROLE_LABELS[effectiveUser?.role]})</span>
              {isViewingAs && <span className="text-amber-600 font-medium"> (Viewing as {USER_ROLE_LABELS[effectiveUser?.role]})</span>}
            </p> */}
          </div>

          {/* Role Selector for ViewAs functionality */}
          <div className="w-full sm:w-auto">
            <RoleSelector className="w-full sm:w-64" />
          </div>

          {eventsData?.events?.length > 0 && (
            <div className="flex flex-col sm:items-end gap-2 w-full sm:w-auto">
              <select
                value={selectedEvent || ''}
                onChange={(e) => setSelectedEvent(e.target.value || null)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm w-full sm:w-auto min-w-0 sm:min-w-48"
              >
                {effectivePermissions.isSuperAdmin() && (
                  <option value="">All Events (System-wide)</option>
                )}
                {eventsData.events.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.name}
                  </option>
                ))}
              </select>
              {selectedEvent && (
                <div className="text-sm font-medium text-gray-700 text-left sm:text-right mt-3 sm:mt-0 flex items-center justify-start sm:justify-end">
                  <span className="bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full border border-blue-100 inline-flex items-center gap-1.5 shadow-sm">
                    <Users className="w-4 h-4" />
                    {eventsData.events.find(e => e.id === selectedEvent)?.attendees_count || 0} Attendees
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {analyticsLoading ? (
          <Loading text="Loading analytics..." />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 sm:gap-6">
              {stats.map((stat, index) => (
                <Card
                  key={index}
                  className={stat.clickable ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}
                  onClick={stat.clickable ? stat.onClick : undefined}
                >
                  <Card.Content className="flex items-center gap-3 sm:gap-4 p-4 sm:p-6">
                    <div className={`p-2 sm:p-3 rounded-lg ${stat.bgColor} flex-shrink-0`}>
                      <stat.icon className={`h-5 w-5 sm:h-6 sm:w-6 ${stat.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm text-gray-600 truncate">{stat.label}</p>
                      {stat.isText ? (
                        <p className="text-sm sm:text-lg font-semibold text-gray-900 break-words leading-tight line-clamp-3 overflow-hidden" title={stat.value}>
                          {stat.value}
                        </p>
                      ) : (
                        <p className="text-xl sm:text-2xl font-bold text-gray-900">{stat.value}</p>
                      )}
                      {stat.subtitle && (
                        <p className="text-xs text-gray-500 mt-1">{stat.subtitle}</p>
                      )}
                      {stat.clickable && (
                        <p className="text-xs text-blue-500 mt-1 hidden sm:block">Click to view details</p>
                      )}
                    </div>
                  </Card.Content>
                </Card>
              ))}
            </div>

            {eventsData?.events?.length === 0 && (
              <Card>
                <Card.Content className="text-center py-12">
                  <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {effectivePermissions.isSuperAdmin() ? 'No Active Events' : 'No Assigned Events'}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {effectivePermissions.isSuperAdmin()
                      ? 'Create your first event to start managing registrations'
                      : 'You have not been assigned to any active events yet. Contact your administrator for access.'
                    }
                  </p>
                  {effectivePermissions.canCreateEvents() && (
                    <Button
                      onClick={() => window.location.href = '/events'}
                      className="mt-4"
                    >
                      Create Event
                    </Button>
                  )}
                </Card.Content>
              </Card>
            )}
          </>
        )}

        {/* Data View Modal */}
        <DataViewModal
          isOpen={modalData.isOpen}
          onClose={closeModal}
          title={modalData.title}
          data={modalData.data}
          isLoading={modalData.isLoading}
          error={modalData.error}
          type={modalData.type}
        />
      </div>
    </Layout>
  );
};

export default Dashboard;
